import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import MetricCard from './components/MetricCard';
import PerformanceChart from './components/PerformanceChart';
import CampaignComparisonChart from './components/CampaignComparisonChart';
import ComplianceMonitor from './components/ComplianceMonitor';
import InsightsPanel from './components/InsightsPanel';
import { useAuth } from '../../contexts/AuthContext';
import twilioService from '../../lib/twilio-service';

const AnalyticsDashboard = () => {
  const { user } = useAuth();
  const [dateRange, setDateRange] = useState('30d');
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [campaignData, setCampaignData] = useState(null);
  const [complianceData, setComplianceData] = useState(null);
  const [insightsData, setInsightsData] = useState(null);
  const [error, setError] = useState(null);

  // Load all dashboard data
  const loadStats = async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Load all data in parallel
      const [
        statsResult,
        performanceResult,
        campaignResult,
        complianceResult,
        insightsResult
      ] = await Promise.all([
        twilioService.getDashboardStats(user.id, dateRange),
        twilioService.getPerformanceTrends(user.id, dateRange),
        twilioService.getCampaignComparison(user.id, dateRange),
        twilioService.getComplianceData(user.id, dateRange),
        twilioService.getAutomatedInsights(user.id, dateRange)
      ]);
      
      if (statsResult.error) {
        console.error('Error loading dashboard stats:', statsResult.error);
        setError('Failed to load dashboard statistics');
        return;
      }
      
      setStats(statsResult.data);
      setPerformanceData(performanceResult.data || []);
      setCampaignData(campaignResult.data || []);
      setComplianceData(complianceResult.data);
      setInsightsData(insightsResult.data || []);
    } catch (err) {
      console.error('Error loading dashboard stats:', err);
      setError('An error occurred while loading statistics');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    loadStats();
  };

  useEffect(() => {
    loadStats();
  }, [user?.id, dateRange]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16 pb-20 md:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl font-bold text-foreground mb-2 tracking-tight">Analytics Dashboard</h1>
            <p className="text-muted-foreground">
              Track your message performance and engagement metrics
            </p>
          </div>

          {/* Filter Controls */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-full sm:w-auto px-4 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary touch-target"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="all">All time</option>
              </select>
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className="w-full sm:w-auto px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 touch-target"
              >
                <Icon name="RefreshCw" size={16} />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-error/10 border border-error/20 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <Icon name="AlertCircle" size={20} className="text-error" />
                <span className="text-error">{error}</span>
              </div>
            </div>
          )}

          {/* Key Metrics Grid */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricCard
                title="Sends"
                value={stats.sends.value}
                change={stats.sends.change}
                changeType={stats.sends.changeType}
                icon="Send"
                description={`Messages sent in the last ${dateRange === '7d' ? '7 days' : dateRange === '30d' ? '30 days' : dateRange === '90d' ? '90 days' : 'period'}`}
              />
              <MetricCard
                title="Replies"
                value={stats.replies.value}
                change={stats.replies.change}
                changeType={stats.replies.changeType}
                icon="MessageSquare"
                description="Client replies received"
              />
              <MetricCard
                title="Reply Rate"
                value={stats.replyRate.value}
                change={stats.replyRate.change}
                changeType={stats.replyRate.changeType}
                icon="TrendingUp"
                description="Percentage of sends that received replies"
              />
              <MetricCard
                title="Total Messages"
                value={stats.total.value}
                change={stats.total.change}
                changeType={stats.total.changeType}
                icon="BarChart3"
                description="Total messages (sends + replies)"
              />
            </div>
          )}

          {/* Empty State */}
          {!stats && !isLoading && !error && (
            <div className="flex flex-col items-center justify-center py-12">
              <Icon name="BarChart3" size={64} className="text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No data available</h3>
              <p className="text-sm text-muted-foreground">
                Start sending messages to see your analytics here
              </p>
            </div>
          )}

          {/* Charts Section */}
          {performanceData && performanceData.length > 0 && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8 mt-8">
              <PerformanceChart
                data={performanceData}
                title="Performance Trends"
                height={350}
              />
              {campaignData && campaignData.length > 0 ? (
                <CampaignComparisonChart
                  data={campaignData}
                  title="Campaign Performance Comparison"
                />
              ) : (
                <div className="bg-card border border-border rounded-lg p-6 flex items-center justify-center">
                  <div className="text-center">
                    <Icon name="BarChart3" size={48} className="text-muted-foreground mb-4 mx-auto" />
                    <p className="text-muted-foreground">Campaign data will appear here</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Compliance and Insights Section */}
          {(complianceData || (insightsData && insightsData.length > 0)) && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
              {complianceData ? (
                <ComplianceMonitor
                  complianceData={complianceData}
                />
              ) : (
                <div className="bg-card border border-border rounded-lg p-6 flex items-center justify-center">
                  <div className="text-center">
                    <Icon name="Shield" size={48} className="text-muted-foreground mb-4 mx-auto" />
                    <p className="text-muted-foreground">Compliance data will appear here</p>
                  </div>
                </div>
              )}
              {insightsData && insightsData.length > 0 ? (
                <InsightsPanel
                  insights={insightsData}
                />
              ) : (
                <div className="bg-card border border-border rounded-lg p-6 flex items-center justify-center">
                  <div className="text-center">
                    <Icon name="Lightbulb" size={48} className="text-muted-foreground mb-4 mx-auto" />
                    <p className="text-muted-foreground">Insights will appear here</p>
                  </div>
                </div>
              )}
            </div>
          )}


          {/* Loading Overlay */}
          {isLoading && (
            <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
              <div className="bg-card border border-border rounded-lg p-6 elevation-4 flex items-center space-x-3">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <span className="text-foreground font-medium">Loading analytics data...</span>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AnalyticsDashboard;